import {useInfiniteQuery} from "react-query";
import {
  ResidentService,
  SORT_RESIDENTS_BY,
} from "../../../services/ResidentServices";
import {useState} from "react";
import {useDebouncedPrimitiveValue} from "../../../hooks/useDebouncedPrimitiveValue";
import {PAGE_SIZE, DEBOUNCE_SEARCH_BY} from "../consts";
import {RESIDENT_SUB_STATUS, ResidentStatus, ResidentSubStatus} from "../types";
import {
  filterSwitchArchived,
  filterSwitchWaitingList,
} from "../helpers/filterHelpers";

/**
 * Query residents via POST request, one page at a time.
 *
 * Pages accumulate rather than the page size growing: asking for more used to refetch the
 * whole list with a larger limit, so showing 300 residents moved 1,650 rows.
 */
export const useFetchResidentsViaPost = (
  searchTerm?: string,
  facility_ids?: string[],
  selectedStatus?: Array<ResidentStatus | ResidentSubStatus>,
) => {
  // By default, residents should be sorted by name. Clicking table header updates the state which triggers new request.
  const [sortBy, setSortBy] =
    useState<(typeof SORT_RESIDENTS_BY)[number]>("name");

  // Default sort direction is ascending
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");

  // Debounce search term to avoid request overload
  const debouncedSearchTerm = useDebouncedPrimitiveValue(
    searchTerm,
    DEBOUNCE_SEARCH_BY,
  );

  const currentSessionStatuses: Array<ResidentStatus | ResidentSubStatus> =
    JSON.parse(localStorage.getItem("currentSessionStatuses")) ||
    selectedStatus;

  // Archived and not archived are not included in the filter array but calculated as flags from checked statuses
  const isArchived = filterSwitchArchived(
    currentSessionStatuses,
    RESIDENT_SUB_STATUS.ARCHIVED,
  );
  const waitingList = filterSwitchWaitingList(
    currentSessionStatuses,
    RESIDENT_SUB_STATUS.WAITING_LIST,
    RESIDENT_SUB_STATUS.NOT_WAITING_LIST,
  );
  const residentStatus = currentSessionStatuses.filter(
    status =>
      status !== RESIDENT_SUB_STATUS.WAITING_LIST &&
      status !== RESIDENT_SUB_STATUS.ARCHIVED &&
      status !== RESIDENT_SUB_STATUS.NOT_ARCHIVED &&
      status !== RESIDENT_SUB_STATUS.NOT_WAITING_LIST,
  );

  const {
    data,
    isFetching: isFetchingResidents,
    fetchNextPage,
    hasNextPage,
  } = useInfiniteQuery(
    // Every filter belongs in the key. Without them a filter change appends a page of new
    // results to the old ones instead of replacing them.
    [
      "search_residents",
      debouncedSearchTerm,
      facility_ids,
      sortBy,
      sortDirection,
      residentStatus,
      isArchived,
      waitingList,
    ],
    async ({pageParam = 0}) => {
      const {data: page} = await ResidentService.getResidentsViaPost({
        facility_ids: facility_ids ?? [],
        search: searchTerm,
        page: pageParam,
        size: PAGE_SIZE,
        sort_by: sortBy,
        sort_direction: sortDirection,
        resident_status: residentStatus,
        is_archived: isArchived,
        waiting_list: waitingList,
      });

      return page;
    },
    {
      getNextPageParam: page =>
        page.number + 1 < page.totalPages ? page.number + 1 : false,
      cacheTime: 0,
      refetchOnMount: false,
    },
  );

  const residents = data?.pages.flatMap(page => page.content) ?? [];

  return {
    residents,
    totalCount: data?.pages[0]?.totalElements ?? 0,
    loadedCount: residents.length,
    hasMoreResidents: Boolean(hasNextPage),
    isFetchingResidents,
    sort: (
      sortBy: (typeof SORT_RESIDENTS_BY)[number],
      sortDirection: "asc" | "desc",
    ) => {
      setSortBy(sortBy);
      setSortDirection(sortDirection);
    },
    loadMoreResidents: () => fetchNextPage(),
  };
};
